import React from 'react';

export interface ButtonProps {
  label?: string;
  variant?: 'primary' | 'neutral' | 'subtle' | 'danger';
  state?: 'default' | 'hover' | 'disabled';
  size?: 'medium' | 'small';
  onClick?: () => void;
  className?: string;
}

const Button: React.FC<ButtonProps> = ({ 
  label = 'Button',
  variant = 'primary',
  state = 'default',
  size = 'medium',
  onClick,
  className = ''
}) => {
  // Base styles - exact px values from Figma
  const baseStyles = 'flex items-center justify-center rounded-200 font-inter font-regular text-body-medium leading-none transition-all';
  
  // Size styles - exact padding from Figma
  const sizeStyles = {
    medium: 'px-300 py-300 gap-200', // 12px padding
    small: 'px-200 py-200 gap-200'   // 8px padding
  };

  // Variant + State combinations - exact colors from Figma
  const getVariantStyles = () => {
    // PRIMARY VARIANT
    if (variant === 'primary') {
      if (state === 'disabled') {
        return 'bg-background-disabled-default border border-border-disabled-default text-text-disabled-on-disabled cursor-not-allowed';
      }
      if (state === 'hover') {
        return 'bg-background-brand-hover border border-border-brand-default text-text-brand-on-brand cursor-pointer';
      }
      // default
      return 'bg-background-brand-default border border-border-brand-default text-text-brand-on-brand hover:bg-background-brand-hover cursor-pointer';
    }
    
    // NEUTRAL VARIANT
    if (variant === 'neutral') {
      if (state === 'disabled') {
        return 'bg-background-disabled-default border border-border-disabled-default text-text-disabled-on-disabled cursor-not-allowed';
      }
      if (state === 'hover') {
        return 'bg-background-neutral-tertiary-hover border border-border-neutral-secondary text-text-default cursor-pointer';
      }
      // default
      return 'bg-background-neutral-tertiary border border-border-neutral-secondary text-text-default hover:bg-background-neutral-tertiary-hover cursor-pointer';
    }
    
    // SUBTLE VARIANT
    if (variant === 'subtle') {
      if (state === 'disabled') {
        return 'bg-background-disabled-default border border-border-disabled-default text-text-disabled-on-disabled cursor-not-allowed';
      }
      if (state === 'hover') {
        return 'border border-border-default-default text-text-default cursor-pointer';
      }
      // default
      return 'text-text-neutral-default hover:border hover:border-border-default-default cursor-pointer';
    }
    
    // DANGER VARIANT
    if (variant === 'danger') {
      if (state === 'disabled') {
        return 'bg-background-disabled-default border border-border-disabled-default text-text-disabled-on-disabled cursor-not-allowed';
      }
      if (state === 'hover') {
        return 'bg-background-danger-hover border border-border-danger-default text-text-danger-on-danger cursor-pointer';
      }
      // default
      return 'bg-background-danger-default border border-border-danger-secondary text-text-danger-on-danger hover:bg-background-danger-hover hover:border-border-danger-default cursor-pointer';
    }
    
    return '';
  };

  const combinedStyles = `${baseStyles} ${sizeStyles[size]} ${getVariantStyles()} ${className}`;

  return (
    <button
      className={combinedStyles}
      onClick={state === 'disabled' ? undefined : onClick}
      disabled={state === 'disabled'}
    >
      <span>{label}</span>
    </button>
  );
};

export default Button;

